import { dirname, isAbsolute, resolve } from 'node:path';
import { existsSync, statSync } from 'node:fs';
import type { StackInfo } from '../synthesis/assembly-reader.js';
import type { TemplateResource } from '../types/resource.js';
import { buildCdkPathIndex, resolveCdkPathToLogicalIds } from '../cli/cdk-path.js';
import { matchStacks } from '../cli/stack-matcher.js';
import {
  formatStateRemedy,
  substituteImagePlaceholders,
  tryResolveImageFnJoin,
  type ImageResolutionContext,
} from './intrinsic-image.js';
import {
  substituteAgainstState,
  substituteAgainstStateAsync,
  resolveWithSensitivity,
  resolveWithSensitivityAsync,
  type SubstitutionContext,
} from './state-resolver.js';
import { getEmbedConfig } from './embed-config.js';

/**
 * Result of resolving a `cdkl run-task <target>` argument back to a
 * concrete `AWS::ECS::TaskDefinition` in the synthesized assembly. The
 * shape mirrors what the ECS API + the CFn template expose so the runner
 * can directly translate each field into `docker run` flags / `docker
 * network` lifecycle calls without re-walking the template.
 *
 * Phase 1: no AWS-side ECS API calls are needed to build this — every
 * field is template-derived. `Secrets[].valueFrom` resolution happens
 * separately in `ecs-secrets-resolver.ts` so this module stays free of
 * AWS SDK imports.
 */
export interface ResolvedEcsTask {
  /** Stack the task definition belongs to. */
  stack: StackInfo;
  /** Logical id of the AWS::ECS::TaskDefinition resource. */
  taskDefinitionLogicalId: string;
  /** Raw template entry — kept for future feature additions. */
  resource: TemplateResource;
  /**
   * Task family. Falls back to the logical id when not declared (CDK auto-
   * generates a family in this case but only at deploy time; locally we
   * surface the logical id so logs are still identifiable).
   */
  family: string;
  /**
   * Default `bridge`. `awsvpc` is mapped to `bridge` locally with a warn
   * because docker cannot emulate ENI-per-task; `host` and `none` pass
   * through unchanged.
   */
  networkMode: 'bridge' | 'awsvpc' | 'host' | 'none';
  /**
   * Resolved task role ARN. Surfaced as either:
   *   - a flat string ARN passed through verbatim from the template, OR
   *   - a synth-time placeholder of the shape
   *     `arn:aws:iam::${AWS::AccountId}:role/<RoleLogicalId>` when the
   *     `TaskRoleArn` is a `Ref` / `Fn::GetAtt` against an `AWS::IAM::Role`
   *     in the same stack. The CLI substitutes the placeholder account
   *     segment lazily via STS `GetCallerIdentity` when (and only when)
   *     `--assume-task-role` is used in its bare form.
   *   - `undefined` when the template's `TaskRoleArn` is missing OR is an
   *     intrinsic that doesn't reference a same-stack IAM Role (e.g.
   *     points at a non-IAM-Role resource type or is an unsupported
   *     intrinsic shape — see `resolveRoleArn`).
   */
  taskRoleArn?: string;
  /** Resolved execution role ARN. Follows the same shape as `taskRoleArn`. */
  executionRoleArn?: string;
  containers: ResolvedEcsContainer[];
  volumes: ResolvedEcsVolume[];
  /**
   * `RuntimePlatform.CpuArchitecture` + `OperatingSystemFamily`. Only
   * `X86_64` / `ARM64` + `LINUX` are routed in v1; other values pass
   * through unchanged but the docker-runner only honors the arch field.
   */
  runtimePlatform?: { cpuArchitecture: 'X86_64' | 'ARM64'; operatingSystemFamily: 'LINUX' };
  /**
   * Resolution warnings (e.g. `awsvpc` → `bridge` map) the caller may
   * want to surface at startup. Non-fatal — the runner still proceeds.
   */
  warnings: string[];
}

export interface ResolvedEcsContainer {
  name: string;
  image: ResolvedEcsImage;
  command?: string[];
  entryPoint?: string[];
  workingDirectory?: string;
  /** Literal-only env vars; intrinsic-valued entries are dropped (matches `cdkl invoke` v1). */
  environment: Record<string, string>;
  /**
   * Env keys whose value resolved to a decrypted `SecureString` SSM
   * parameter (issue #99). The runner unions these into the
   * value-from-process-env set so the secret never lands on the
   * `docker run` argv.
   */
  sensitiveEnvKeys: string[];
  /** SecretArn entries. Resolved to real values by `ecs-secrets-resolver.ts`. */
  secrets: { name: string; valueFrom: string }[];
  /**
   * Container port mappings. `name` (mirrors the CFn `Name` field) is
   * surfaced for Service Connect resolution (Phase 3 of #262 / Issue
   * #460) — the resolver matches `ServiceConnectConfiguration.Services[].PortName`
   * against this field.
   */
  portMappings: {
    name?: string;
    containerPort: number;
    hostPort?: number;
    protocol: 'tcp' | 'udp';
  }[];
  mountPoints: { sourceVolume: string; containerPath: string; readOnly: boolean }[];
  dependsOn: { containerName: string; condition: 'START' | 'COMPLETE' | 'SUCCESS' | 'HEALTHY' }[];
  links: string[];
  /** Default true per AWS docs — when not set, the container is treated as essential. */
  essential: boolean;
  healthCheck?: {
    command: string[];
    interval?: number;
    timeout?: number;
    retries?: number;
    startPeriod?: number;
  };
  user?: string;
  privileged?: boolean;
  readonlyRootFilesystem?: boolean;
  ulimits: { name: string; softLimit: number; hardLimit: number }[];
  /**
   * Non-fatal warnings produced while parsing this container — typically
   * intrinsic-valued env vars or secret ValueFrom entries that could not
   * be substituted against state. The CLI prints these so the user
   * understands why an expected env / secret is missing.
   */
  warnings: string[];
}

export type ResolvedEcsImage =
  | { kind: 'cdk-asset'; assetHash?: string }
  | { kind: 'ecr'; uri: string; account: string; region: string }
  | { kind: 'public'; uri: string };

export interface ResolvedEcsVolume {
  name: string;
  kind: 'host' | 'docker';
  /** Absolute host path for a bind mount. Undefined → docker anonymous volume. */
  hostPath?: string;
  /**
   * `DockerVolumeConfiguration`. When set, the volume is realized via
   * `docker volume create` rather than a bind mount.
   */
  dockerVolumeConfig?: {
    scope: 'task' | 'shared';
    autoprovision?: boolean;
    driver?: string;
    driverOpts?: Record<string, string>;
    labels?: Record<string, string>;
  };
}

export class EcsTaskResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EcsTaskResolutionError';
    Object.setPrototypeOf(this, EcsTaskResolutionError.prototype);
  }
}

/**
 * The partition table + its lookup now live in `./intrinsic-image.js`,
 * which is the ONE home for them in this package (issue #575,
 * go-to-k/cdkd#1821: a second copy behind
 * `derivePseudoParametersFromRegion` there had drifted three rows behind
 * this one, so a `us-isof-` / `eu-isoe-` / `eusc-` region resolved
 * commercial on that path). `intrinsic-image.ts` is a leaf module and
 * this file already imports from it, so the reverse direction would be
 * an import cycle.
 *
 * `derivePartitionAndUrlSuffix` is re-exported so the existing importers
 * (`local-invoke.ts`, `ecs-service-emulator.ts`, `local-run-task.ts`,
 * `local-start-api.ts`) keep working unchanged. `PARTITION_TABLE` is NOT
 * inherited traffic -- it did not exist before this change -- and is
 * carried along only so the pair stays reachable from one module; its
 * sole consumer today is the test asserting the two names resolve to the
 * SAME objects rather than to a second copy.
 */
export { PARTITION_TABLE, derivePartitionAndUrlSuffix } from './intrinsic-image.js';

/**
 * Optional substitution data fed into `parseContainerImage`. Closes issue
 * #264 — container `Image` fields shaped as `Fn::Sub` against AWS pseudo
 * parameters (`${AWS::AccountId}` / `${AWS::Region}` / `${AWS::Partition}` /
 * `${AWS::URLSuffix}`) and / or same-stack `AWS::ECR::Repository` refs are
 * resolvable at runtime when the caller can supply the account ID + region
 * (Tier 1, no state needed) and optionally the host state-recorded
 * `physicalId` map (Tier 2, `--from-state`).
 *
 * The CLI command resolves both blocks lazily — STS is only invoked when
 * at least one container's `Image` references the pseudo parameters — and
 * passes the resolved shape here. The resolver itself stays pure and
 * synchronous.
 */
/**
 * Substitution context for ECS resolution. Re-exported alias for the
 * shared `ImageResolutionContext` in `intrinsic-image.ts` (extracted
 * in issue #286 Gap 2 when `lambda-resolver.ts` needed the same
 * resolver). The shared type carries `pseudoParameters` (Tier 1) +
 * `stateResources` (Tier 2). `stateResources` is consumed by:
 *   - Image (PR #267): `${<LogicalId>}` against an `AWS::ECR::Repository`
 *     and the `Fn::GetAtt: [<Repo>, 'RepositoryUri']` shape.
 *   - Environment / Secrets (issue #291): intrinsic-valued
 *     `Environment[].Value` and `Secrets[].ValueFrom` entries
 *     (`Ref` / `Fn::GetAtt` / `Fn::Sub` / `Fn::Join`) are substituted
 *     via `state-resolver.ts`'s `substituteAgainstState`.
 * Existing consumers (`src/cli/commands/local-run-task.ts`) import the
 * alias; new code should reach for `ImageResolutionContext` directly.
 */
export type EcsImageResolutionContext = ImageResolutionContext;

/**
 * Parse a `target` argument into (optional stack pattern, path-or-id).
 * Mirrors `lambda-resolver.parseTarget` exactly — same accepted forms,
 * same single-stack auto-detect rule.
 */
export interface ParsedEcsTarget {
  stackPattern: string | null;
  pathOrId: string;
  isPath: boolean;
}

/**
 * Walk the matched stack's template and report whether any container's
 * `Image` field needs Tier 1 (pseudo-parameter) or Tier 2 (state-recorded
 * ECR Repository) substitution. The CLI uses this to make the STS /
 * state-load calls lazy — flat strings / cdk-asset shapes / Fn::Sub bodies
 * with no recognized placeholders skip both calls entirely.
 *
 * The probe is per-stack rather than per-target because we don't run the
 * full target resolver until the substitution context is built; cheap
 * O(resources) scan over the template's task definitions is sufficient.
 */
export interface EcsImageResolutionNeeds {
  /** Any `Fn::Sub` body references an `AWS::*` pseudo parameter. */
  needsPseudoParameters: boolean;
  /**
   * Any `Fn::Sub` body references an `AWS::ECR::Repository` logical ID,
   * OR any `Fn::GetAtt: [<Repo>, 'RepositoryUri' | 'Arn']` is present.
   */
  needsStateResources: boolean;
  /**
   * Any container's `Environment[].Value` OR `Secrets[].ValueFrom` is
   * an intrinsic (`Ref` / `Fn::GetAtt` / `Fn::Sub` / `Fn::Join`); OR
   * any task's `Volumes[].Host.SourcePath` is an intrinsic (Gap 6 of
   * #286); OR any task's `TaskRoleArn` / `ExecutionRoleArn` is a
   * `Fn::Sub` / `Fn::Join` intrinsic (Gap 5 of #286). The CLI uses
   * this flag to gate `--from-state` state loading + pseudo-parameter
   * resolution. Without `--from-state` these entries either drop with
   * warnings (env / secret / role ARN) or hard-error (volume source
   * path — silent drop would mount an empty anonymous volume).
   */
  needsEnvOrSecretSubstitution: boolean;
  /**
   * Any container's `Environment[].Value` OR `Secrets[].ValueFrom` is a
   * cross-stack intrinsic (top-level `Fn::ImportValue` /
   * `Fn::GetStackOutput`). Issue #454 — gates the cross-stack resolver
   * construction inside the `--from-state` flow so literal +
   * same-stack-intrinsic env maps don't pay the extra S3-client / index-
   * load cost.
   */
  needsCrossStackResolver: boolean;
}

export function detectEcsImageResolutionNeeds(stack: StackInfo): EcsImageResolutionNeeds {
  const resources = stack.template.Resources ?? {};
  let needsPseudoParameters = false;
  let needsStateResources = false;
  let needsEnvOrSecretSubstitution = false;
  let needsCrossStackResolver = false;

  for (const res of Object.values(resources)) {
    if (res.Type !== 'AWS::ECS::TaskDefinition') continue;
    const props = res.Properties ?? {};
    const containers = Array.isArray(props['ContainerDefinitions'])
      ? props['ContainerDefinitions']
      : [];
    for (const c of containers) {
      if (!c || typeof c !== 'object') continue;
      const co = c as Record<string, unknown>;
      const image = co['Image'];
      const need = inspectImageForSubstitutions(image, resources);
      if (need.pseudo) needsPseudoParameters = true;
      if (need.state) needsStateResources = true;
      if (containerHasIntrinsicEnvOrSecret(co)) needsEnvOrSecretSubstitution = true;
      if (containerHasCrossStackEnvOrSecret(co)) {
        needsEnvOrSecretSubstitution = true;
        needsCrossStackResolver = true;
      }
    }
    // Volumes[].Host.SourcePath as a CFn intrinsic (Gap 6 of #286).
    const rawVolumes = props['Volumes'];
    if (Array.isArray(rawVolumes)) {
      for (const v of rawVolumes) {
        if (volumeHasIntrinsicSourcePath(v)) {
          needsEnvOrSecretSubstitution = true;
          break;
        }
      }
    }
    // TaskRoleArn / ExecutionRoleArn as a Fn::Sub / Fn::Join intrinsic
    // (Gap 5 of #286). Ref / Fn::GetAtt shapes against AWS::IAM::Role
    // resolve to a placeholder ARN without state, so they don't trigger
    // state loading.
    if (
      isFnJoinOrSubIntrinsic(props['TaskRoleArn']) ||
      isFnJoinOrSubIntrinsic(props['ExecutionRoleArn'])
    ) {
      needsEnvOrSecretSubstitution = true;
    }
  }
  return {
    needsPseudoParameters,
    needsStateResources,
    needsEnvOrSecretSubstitution,
    needsCrossStackResolver,
  };
}

/**
 * Returns true when any `Environment[].Value` or `Secrets[].ValueFrom`
 * is a top-level `Fn::ImportValue` / `Fn::GetStackOutput` intrinsic.
 * Issue #454 — gates cross-stack resolver construction so literal +
 * same-stack-intrinsic env / secret maps don't pay the extra cost.
 */
function containerHasCrossStackEnvOrSecret(c: Record<string, unknown>): boolean {
  const env = c['Environment'];
  if (Array.isArray(env)) {
    for (const entry of env) {
      if (!entry || typeof entry !== 'object') continue;
      const v = (entry as Record<string, unknown>)['Value'];
      if (isCrossStackIntrinsic(v)) return true;
    }
  }
  const secrets = c['Secrets'];
  if (Array.isArray(secrets)) {
    for (const entry of secrets) {
      if (!entry || typeof entry !== 'object') continue;
      const v = (entry as Record<string, unknown>)['ValueFrom'];
      if (isCrossStackIntrinsic(v)) return true;
    }
  }
  return false;
}

function isCrossStackIntrinsic(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return 'Fn::ImportValue' in obj || 'Fn::GetStackOutput' in obj;
}

/**
 * Detect whether a Volume entry has an intrinsic-valued `Host.SourcePath`.
 * Used by `detectEcsImageResolutionNeeds` (Gap 6 of #286) to trigger
 * state-load + pseudo-parameter resolution under `--from-state` when the
 * SourcePath is built via `cdk.Fn.sub(...)` / `cdk.Fn.join(...)`.
 */
function volumeHasIntrinsicSourcePath(v: unknown): boolean {
  if (!v || typeof v !== 'object') return false;
  const host = (v as Record<string, unknown>)['Host'];
  if (!host || typeof host !== 'object') return false;
  const sp = (host as Record<string, unknown>)['SourcePath'];
  if (sp === undefined || sp === null) return false;
  if (typeof sp === 'string') return false;
  return typeof sp === 'object';
}

/**
 * Detect whether a value is an `Fn::Sub` / `Fn::Join` intrinsic. Used by
 * `detectEcsImageResolutionNeeds` to flag role-ARN intrinsic shapes
 * (Gap 5 of #286) that need `--from-state` + pseudo-parameter
 * substitution. `Ref` / `Fn::GetAtt` shapes resolve to a placeholder
 * ARN without state and are intentionally excluded here.
 */
function isFnJoinOrSubIntrinsic(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return 'Fn::Join' in obj || 'Fn::Sub' in obj;
}

/**
 * Returns true when any `Environment[].Value` or `Secrets[].ValueFrom`
 * is an intrinsic (non-literal). Used to gate `--from-state` state
 * loading at the CLI layer — issue #291.
 */
function containerHasIntrinsicEnvOrSecret(c: Record<string, unknown>): boolean {
  const env = c['Environment'];
  if (Array.isArray(env)) {
    for (const entry of env) {
      if (!entry || typeof entry !== 'object') continue;
      const v = (entry as Record<string, unknown>)['Value'];
      if (
        v !== undefined &&
        typeof v !== 'string' &&
        typeof v !== 'number' &&
        typeof v !== 'boolean'
      ) {
        return true;
      }
    }
  }
  const secrets = c['Secrets'];
  if (Array.isArray(secrets)) {
    for (const entry of secrets) {
      if (!entry || typeof entry !== 'object') continue;
      const v = (entry as Record<string, unknown>)['ValueFrom'];
      if (v !== undefined && typeof v !== 'string') return true;
    }
  }
  return false;
}

function inspectImageForSubstitutions(
  image: unknown,
  resources: Record<string, TemplateResource>
): { pseudo: boolean; state: boolean } {
  if (!image || typeof image !== 'object') return { pseudo: false, state: false };
  const obj = image as Record<string, unknown>;

  // Fn::GetAtt direct shape against an ECR::Repository — Tier 2 only.
  const getAtt = obj['Fn::GetAtt'];
  if (getAtt !== undefined) {
    let lid: string | undefined;
    if (Array.isArray(getAtt) && typeof getAtt[0] === 'string') lid = getAtt[0];
    else if (typeof getAtt === 'string') lid = getAtt.split('.')[0];
    if (lid && resources[lid]?.Type === 'AWS::ECR::Repository') {
      return { pseudo: false, state: true };
    }
  }

  // Fn::Join body: recursively walk every element for references that
  // would trigger Tier 1 (pseudo) / Tier 2 (state) needs. CDK 2.x
  // synthesizes `ContainerImage.fromEcrRepository(repo)` as a Fn::Join
  // containing nested Fn::Select / Fn::Split over the repo's Arn GetAtt
  // plus a Ref to the repo and `Ref: AWS::URLSuffix`.
  const join = obj['Fn::Join'];
  if (Array.isArray(join) && join.length === 2 && Array.isArray(join[1])) {
    const scan: { pseudo: boolean; state: boolean } = { pseudo: false, state: false };
    inspectIntrinsicNeeds(join[1], resources, scan);
    if (scan.pseudo || scan.state) return scan;
  }

  // Fn::Sub body: scan every `${...}` placeholder.
  let sub: string | undefined;
  const subRaw = obj['Fn::Sub'];
  if (typeof subRaw === 'string') sub = subRaw;
  else if (Array.isArray(subRaw) && typeof subRaw[0] === 'string') sub = subRaw[0];
  if (!sub) return { pseudo: false, state: false };

  let pseudo = false;
  let state = false;
  const placeholderRegex = /\$\{([^}]+)\}/g;
  let m: RegExpExecArray | null;
  while ((m = placeholderRegex.exec(sub)) !== null) {
    const key = m[1]!;
    if (key.startsWith('AWS::')) {
      pseudo = true;
      continue;
    }
    const dot = key.indexOf('.');
    const lid = dot === -1 ? key : key.slice(0, dot);
    if (resources[lid]?.Type === 'AWS::ECR::Repository') state = true;
  }
  return { pseudo, state };
}

/**
 * Recursive needs probe used by `inspectImageForSubstitutions` for the
 * `Fn::Join` shape: walk every nested intrinsic and flag whether a
 * `Ref: AWS::*` pseudo parameter is reachable (Tier 1 needed) or a
 * `Ref` / `Fn::GetAtt` against an `AWS::ECR::Repository` is reachable
 * (Tier 2 needed).
 */
function inspectIntrinsicNeeds(
  node: unknown,
  resources: Record<string, TemplateResource>,
  scan: { pseudo: boolean; state: boolean }
): void {
  if (node === null || node === undefined) return;
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') return;
  if (Array.isArray(node)) {
    for (const item of node) inspectIntrinsicNeeds(item, resources, scan);
    return;
  }
  if (typeof node !== 'object') return;
  const obj = node as Record<string, unknown>;

  if (typeof obj['Ref'] === 'string') {
    const target = obj['Ref'];
    if (target.startsWith('AWS::')) scan.pseudo = true;
    else if (resources[target]?.Type === 'AWS::ECR::Repository') scan.state = true;
    return;
  }

  const getAtt = obj['Fn::GetAtt'];
  if (getAtt !== undefined) {
    let lid: string | undefined;
    if (Array.isArray(getAtt) && typeof getAtt[0] === 'string') lid = getAtt[0];
    else if (typeof getAtt === 'string') lid = getAtt.split('.')[0];
    if (lid && resources[lid]?.Type === 'AWS::ECR::Repository') scan.state = true;
    return;
  }

  // For every other intrinsic shape, descend into its arguments so a
  // `Fn::Select` / `Fn::Split` / `Fn::Join` / `Fn::Sub` wrapper does
  // not hide a reference deeper in the tree.
  for (const value of Object.values(obj)) {
    inspectIntrinsicNeeds(value, resources, scan);
  }
}

export function parseEcsTarget(target: string): ParsedEcsTarget {
  if (typeof target !== 'string' || target.length === 0) {
    throw new EcsTaskResolutionError(
      "Empty target. Pass a CDK display path (e.g. 'MyStack/MyService/TaskDef') or stack-qualified logical ID (e.g. 'MyStack:MyServiceTaskDefXYZ1234')."
    );
  }
  const colonIdx = target.indexOf(':');
  const slashIdx = target.indexOf('/');
  if (colonIdx > 0 && (slashIdx === -1 || colonIdx < slashIdx)) {
    const stackPattern = target.substring(0, colonIdx);
    const pathOrId = target.substring(colonIdx + 1);
    if (pathOrId.length === 0) {
      throw new EcsTaskResolutionError(`Target '${target}' has no logical ID after ':'.`);
    }
    return { stackPattern, pathOrId, isPath: pathOrId.includes('/') };
  }
  if (slashIdx > 0) {
    return { stackPattern: target.substring(0, slashIdx), pathOrId: target, isPath: true };
  }
  return { stackPattern: null, pathOrId: target, isPath: false };
}

/**
 * Resolve a parsed target against the synthesized stacks. Throws
 * `EcsTaskResolutionError` with an actionable message (listing every
 * available task definition in the matched stack) on any miss.
 *
 * Optional `context` (issue #264): when the caller can supply AWS
 * pseudo-parameter values (Tier 1) and / or host state-recorded resources
 * (Tier 2), `Fn::Sub`-shaped ECR image URIs that reference
 * `${AWS::AccountId}` / `${AWS::Region}` / a same-stack
 * `AWS::ECR::Repository` are substituted before classification.
 */
export function resolveEcsTaskTarget(
  target: string,
  stacks: StackInfo[],
  context?: EcsImageResolutionContext
): ResolvedEcsTask {
  if (stacks.length === 0) {
    throw new EcsTaskResolutionError('No stacks found in the synthesized assembly.');
  }
  const parsed = parseEcsTarget(target);
  const stack = pickStack(parsed, stacks);
  const resources = stack.template.Resources ?? {};

  let logicalId: string | undefined;
  let resource: TemplateResource | undefined;

  if (parsed.isPath) {
    const index = buildCdkPathIndex(stack.template);
    const resolved = resolveCdkPathToLogicalIds(parsed.pathOrId, index);
    const taskDefs = resolved.filter(
      ({ logicalId: l }) => resources[l]?.Type === 'AWS::ECS::TaskDefinition'
    );
    if (taskDefs.length === 0) {
      throw notFoundError(target, stack, resources);
    }
    if (taskDefs.length > 1) {
      throw new EcsTaskResolutionError(
        `Target '${target}' matches ${taskDefs.length} task definitions in ${stack.stackName}: ` +
          taskDefs.map((t) => t.logicalId).join(', ') +
          '. Refine the path or use the stack:LogicalId form.'
      );
    }
    logicalId = taskDefs[0]!.logicalId;
    resource = resources[logicalId];
  } else {
    resource = resources[parsed.pathOrId];
    if (!resource) throw notFoundError(target, stack, resources);
    logicalId = parsed.pathOrId;
  }

  if (!logicalId || !resource) throw notFoundError(target, stack, resources);

  if (resource.Type === 'AWS::Lambda::Function') {
    throw new EcsTaskResolutionError(
      `Resource '${logicalId}' in ${stack.stackName} is a Lambda function, not an ECS task definition. ` +
        `Use \`${getEmbedConfig().cliName} invoke\` for Lambda; \`${getEmbedConfig().cliName} run-task\` is ECS only.`
    );
  }
  if (resource.Type !== 'AWS::ECS::TaskDefinition') {
    throw new EcsTaskResolutionError(
      `Resource '${logicalId}' in ${stack.stackName} is ${resource.Type}, not an AWS::ECS::TaskDefinition.`
    );
  }

  return extractTaskDefinitionProperties(stack, logicalId, resource, context);
}

function pickStack(parsed: ParsedEcsTarget, stacks: StackInfo[]): StackInfo {
  if (parsed.stackPattern === null) {
    if (stacks.length === 1) return stacks[0]!;
    throw new EcsTaskResolutionError(
      `Multiple stacks in app, target '${parsed.pathOrId}' is missing a stack prefix. ` +
        `Use 'StackName:${parsed.pathOrId}' or 'StackName/...' (path form). ` +
        `Available stacks: ${stacks.map((s) => s.stackName).join(', ')}.`
    );
  }
  const matched = matchStacks(stacks, [parsed.stackPattern]);
  if (matched.length === 0) {
    throw new EcsTaskResolutionError(
      `Stack '${parsed.stackPattern}' not found. ` +
        `Available stacks: ${stacks.map((s) => s.stackName).join(', ')}.`
    );
  }
  if (matched.length > 1) {
    throw new EcsTaskResolutionError(
      `Stack pattern '${parsed.stackPattern}' matched ${matched.length} stacks: ` +
        matched.map((s) => s.stackName).join(', ') +
        '. Use a more specific pattern.'
    );
  }
  return matched[0]!;
}

function extractTaskDefinitionProperties(
  stack: StackInfo,
  logicalId: string,
  resource: TemplateResource,
  context?: EcsImageResolutionContext
): ResolvedEcsTask {
  const props = resource.Properties ?? {};
  const warnings: string[] = [];

  const family = pickString(props['Family']) ?? logicalId;

  const rawNetworkMode = pickString(props['NetworkMode']) ?? 'bridge';
  let networkMode: ResolvedEcsTask['networkMode'];
  if (
    rawNetworkMode === 'bridge' ||
    rawNetworkMode === 'awsvpc' ||
    rawNetworkMode === 'host' ||
    rawNetworkMode === 'none'
  ) {
    networkMode = rawNetworkMode;
  } else {
    throw new EcsTaskResolutionError(
      `Task definition '${logicalId}' has unsupported NetworkMode '${rawNetworkMode}'. ` +
        'Supported values: bridge / awsvpc / host / none.'
    );
  }
  if (networkMode === 'awsvpc') {
    warnings.push(
      `NetworkMode 'awsvpc' on '${logicalId}' is mapped to docker bridge locally — ` +
        'docker cannot emulate ENI-per-task. AWS SDK calls still reach public endpoints via the developer network.'
    );
  }

  // Issue #544 — `ProxyConfiguration` (custom App Mesh / Envoy bootstrap)
  // is NOT honored by `cdkl start-service` / `cdkl run-task`
  // in v1. Design § 2 hard-rejects custom Envoy bootstrap; locally we
  // run the user's containers without the configured proxy and surface
  // a warn so users aren't surprised by the missing sidecar.
  if (props['ProxyConfiguration']) {
    warnings.push(
      `Task definition '${logicalId}' declares 'ProxyConfiguration' (custom Envoy / App Mesh bootstrap), ` +
        `which is NOT honored by '${getEmbedConfig().cliName} start-service' / '${getEmbedConfig().cliName} run-task' in v1. ` +
        'Local execution will run without the configured proxy. See design doc § 2 for the rationale.'
    );
  }

  const resources = stack.template.Resources ?? {};

  const subContext = buildSubstitutionContextFromImageContext(context);
  const taskRoleArn = resolveRoleArn(props['TaskRoleArn'], resources, subContext);
  const executionRoleArn = resolveRoleArn(props['ExecutionRoleArn'], resources, subContext);

  const runtimePlatform = parseRuntimePlatform(props['RuntimePlatform']);

  const rawContainers = props['ContainerDefinitions'];
  if (!Array.isArray(rawContainers) || rawContainers.length === 0) {
    throw new EcsTaskResolutionError(`Task definition '${logicalId}' has no ContainerDefinitions.`);
  }
  const containers = rawContainers.map((c, idx) =>
    parseContainerDefinition(c, idx, logicalId, resources, stack, context)
  );

  // Surface per-container warnings (e.g. dropped intrinsic env vars /
  // secrets) on the task-level `warnings` array so the CLI prints them.
  for (const ctr of containers) {
    for (const w of ctr.warnings) {
      warnings.push(`Container '${ctr.name}': ${w}`);
    }
  }

  const rawVolumes = props['Volumes'];
  const volumes = Array.isArray(rawVolumes)
    ? rawVolumes.map((v, idx) => parseVolume(v, idx, logicalId, subContext))
    : [];

  // dependsOn must reference an existing container name.
  const containerNames = new Set(containers.map((c) => c.name));
  for (const c of containers) {
    for (const d of c.dependsOn) {
      if (!containerNames.has(d.containerName)) {
        throw new EcsTaskResolutionError(
          `Container '${c.name}' depends on '${d.containerName}', which is not defined in task '${logicalId}'.`
        );
      }
    }
  }

  const out: ResolvedEcsTask = {
    stack,
    taskDefinitionLogicalId: logicalId,
    resource,
    family,
    networkMode,
    containers,
    volumes,
    warnings,
  };
  if (taskRoleArn !== undefined) out.taskRoleArn = taskRoleArn;
  if (executionRoleArn !== undefined) out.executionRoleArn = executionRoleArn;
  if (runtimePlatform !== undefined) out.runtimePlatform = runtimePlatform;
  return out;
}

function parseRuntimePlatform(value: unknown): ResolvedEcsTask['runtimePlatform'] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const obj = value as Record<string, unknown>;
  const cpu = obj['CpuArchitecture'];
  const os = obj['OperatingSystemFamily'];
  if (typeof cpu !== 'string' || typeof os !== 'string') return undefined;
  if (cpu !== 'X86_64' && cpu !== 'ARM64') return undefined;
  if (os !== 'LINUX') return undefined;
  return { cpuArchitecture: cpu, operatingSystemFamily: os };
}

function parseContainerDefinition(
  raw: unknown,
  idx: number,
  taskLogicalId: string,
  resources: Record<string, TemplateResource>,
  stack: StackInfo,
  context?: EcsImageResolutionContext
): ResolvedEcsContainer {
  if (!raw || typeof raw !== 'object') {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' ContainerDefinitions[${idx}] is not an object.`
    );
  }
  const c = raw as Record<string, unknown>;
  const name = pickString(c['Name']);
  if (!name) {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' ContainerDefinitions[${idx}] has no Name.`
    );
  }

  const image = parseContainerImage(c['Image'], name, taskLogicalId, resources, stack, context);

  const command = pickStringArray(c['Command']);
  const entryPoint = pickStringArray(c['EntryPoint']);
  const workingDirectory = pickString(c['WorkingDirectory']);

  const subContext = buildSubstitutionContextFromImageContext(context);
  const environment: Record<string, string> = {};
  const sensitiveEnvKeys: string[] = [];
  const droppedEnvKeys: { key: string; reason: string }[] = [];
  if (Array.isArray(c['Environment'])) {
    for (const entry of c['Environment'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const key = pickString(e['Name']);
      const value = e['Value'];
      if (!key) continue;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        environment[key] = String(value);
        continue;
      }
      // Intrinsic-valued entry. With `--from-state` we try to substitute
      // against state + pseudo parameters (closes #291); without it the
      // value is dropped and a warn is surfaced via the task's
      // `warnings` array (matches PR 1 `cdkl invoke` semantics).
      if (subContext) {
        const { result: sub, consumedSensitive } = resolveWithSensitivity(value, subContext);
        if (sub.kind === 'literal') {
          environment[key] = String(sub.value);
          // SecureString-backed value — keep it off the `docker run` argv (#99).
          if (consumedSensitive) sensitiveEnvKeys.push(key);
          continue;
        }
        droppedEnvKeys.push({ key, reason: sub.reason });
      } else {
        droppedEnvKeys.push({
          key,
          reason: 'intrinsic-valued; pass --from-state to substitute against deployed state',
        });
      }
    }
  }

  const secrets: ResolvedEcsContainer['secrets'] = [];
  const droppedSecretKeys: { key: string; reason: string }[] = [];
  if (Array.isArray(c['Secrets'])) {
    for (const entry of c['Secrets'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const sName = pickString(e['Name']);
      const valueFromRaw = e['ValueFrom'];
      if (!sName) continue;
      // Literal string ValueFrom (pre-resolved, or fromSecretCompleteArn).
      if (typeof valueFromRaw === 'string' && valueFromRaw.length > 0) {
        secrets.push({ name: sName, valueFrom: valueFromRaw });
        continue;
      }
      // Intrinsic-valued (`Ref` / `Fn::GetAtt` / `Fn::Join` / `Fn::Sub`).
      // With `--from-state` substitute against deployed state + pseudo
      // parameters; without it, drop the secret and warn — the user's
      // workaround is `fromSecretCompleteArn` (literal ARN at synth time).
      if (subContext) {
        const sub = substituteAgainstState(valueFromRaw, subContext);
        if (sub.kind === 'literal' && typeof sub.value === 'string' && sub.value.length > 0) {
          secrets.push({ name: sName, valueFrom: sub.value });
          continue;
        }
        droppedSecretKeys.push({
          key: sName,
          reason: sub.kind === 'literal' ? 'resolved to non-string / empty value' : sub.reason,
        });
      } else {
        droppedSecretKeys.push({
          key: sName,
          reason: 'intrinsic-valued ValueFrom; pass --from-state to resolve the deployed ARN',
        });
      }
    }
  }

  const portMappings: ResolvedEcsContainer['portMappings'] = [];
  if (Array.isArray(c['PortMappings'])) {
    for (const entry of c['PortMappings'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const p = entry as Record<string, unknown>;
      const containerPort = typeof p['ContainerPort'] === 'number' ? p['ContainerPort'] : undefined;
      if (containerPort === undefined) continue;
      const hostPort = typeof p['HostPort'] === 'number' ? p['HostPort'] : undefined;
      const protocol = pickString(p['Protocol']) === 'udp' ? 'udp' : 'tcp';
      const pm: ResolvedEcsContainer['portMappings'][number] = { containerPort, protocol };
      if (hostPort !== undefined) pm.hostPort = hostPort;
      const portName = typeof p['Name'] === 'string' ? p['Name'] : undefined;
      if (portName !== undefined) pm.name = portName;
      portMappings.push(pm);
    }
  }

  const mountPoints: ResolvedEcsContainer['mountPoints'] = [];
  if (Array.isArray(c['MountPoints'])) {
    for (const entry of c['MountPoints'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const m = entry as Record<string, unknown>;
      const sourceVolume = pickString(m['SourceVolume']);
      const containerPath = pickString(m['ContainerPath']);
      if (!sourceVolume || !containerPath) continue;
      mountPoints.push({
        sourceVolume,
        containerPath,
        readOnly: m['ReadOnly'] === true,
      });
    }
  }

  const dependsOn: ResolvedEcsContainer['dependsOn'] = [];
  if (Array.isArray(c['DependsOn'])) {
    for (const entry of c['DependsOn'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const d = entry as Record<string, unknown>;
      const containerName = pickString(d['ContainerName']);
      const condition = pickString(d['Condition']);
      if (!containerName || !condition) continue;
      if (
        condition !== 'START' &&
        condition !== 'COMPLETE' &&
        condition !== 'SUCCESS' &&
        condition !== 'HEALTHY'
      ) {
        throw new EcsTaskResolutionError(
          `Container '${name}' has invalid DependsOn condition '${condition}'. ` +
            'Accepted values: START / COMPLETE / SUCCESS / HEALTHY.'
        );
      }
      dependsOn.push({ containerName, condition });
    }
  }

  const links = pickStringArray(c['Links']) ?? [];
  const essential = c['Essential'] === false ? false : true;

  let healthCheck: ResolvedEcsContainer['healthCheck'] | undefined;
  if (c['HealthCheck'] && typeof c['HealthCheck'] === 'object') {
    const h = c['HealthCheck'] as Record<string, unknown>;
    const command2 = pickStringArray(h['Command']);
    if (command2 && command2.length > 0) {
      healthCheck = { command: command2 };
      if (typeof h['Interval'] === 'number') healthCheck.interval = h['Interval'];
      if (typeof h['Timeout'] === 'number') healthCheck.timeout = h['Timeout'];
      if (typeof h['Retries'] === 'number') healthCheck.retries = h['Retries'];
      if (typeof h['StartPeriod'] === 'number') healthCheck.startPeriod = h['StartPeriod'];
    }
  }

  const user = pickString(c['User']);
  const privileged = c['Privileged'] === true ? true : undefined;
  const readonlyRootFilesystem = c['ReadonlyRootFilesystem'] === true ? true : undefined;

  const ulimits: ResolvedEcsContainer['ulimits'] = [];
  if (Array.isArray(c['Ulimits'])) {
    for (const entry of c['Ulimits'] as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const u = entry as Record<string, unknown>;
      const uName = pickString(u['Name']);
      const soft = typeof u['SoftLimit'] === 'number' ? u['SoftLimit'] : undefined;
      const hard = typeof u['HardLimit'] === 'number' ? u['HardLimit'] : undefined;
      if (!uName || soft === undefined || hard === undefined) continue;
      ulimits.push({ name: uName, softLimit: soft, hardLimit: hard });
    }
  }

  const warnings: string[] = [];
  for (const d of droppedEnvKeys) {
    warnings.push(`Environment '${d.key}' dropped: ${d.reason}`);
  }
  for (const d of droppedSecretKeys) {
    warnings.push(`Secret '${d.key}' dropped: ${d.reason}`);
  }

  const out: ResolvedEcsContainer = {
    name,
    image,
    environment,
    sensitiveEnvKeys,
    secrets,
    portMappings,
    mountPoints,
    dependsOn,
    links,
    essential,
    ulimits,
    warnings,
  };
  if (command !== undefined) out.command = command;
  if (entryPoint !== undefined) out.entryPoint = entryPoint;
  if (workingDirectory !== undefined) out.workingDirectory = workingDirectory;
  if (healthCheck !== undefined) out.healthCheck = healthCheck;
  if (user !== undefined) out.user = user;
  if (privileged !== undefined) out.privileged = privileged;
  if (readonlyRootFilesystem !== undefined) out.readonlyRootFilesystem = readonlyRootFilesystem;
  return out;
}

/**
 * Map the ECS image-resolution context's `stateResources` +
 * `pseudoParameters` into the shape `substituteAgainstState` expects
 * (closes #291). Returns `undefined` when state has not been loaded —
 * the caller falls back to the pre-PR literal-only path.
 */
function buildSubstitutionContextFromImageContext(
  context: EcsImageResolutionContext | undefined
): SubstitutionContext | undefined {
  if (!context?.stateResources) return undefined;
  const subContext: SubstitutionContext = { resources: context.stateResources };
  if (context.pseudoParameters) {
    subContext.pseudoParameters = { ...context.pseudoParameters };
  }
  if (context.stateParameters) {
    subContext.parameters = { ...context.stateParameters };
  }
  if (context.stateSensitiveParameters?.length) {
    subContext.sensitiveParameters = new Set(context.stateSensitiveParameters);
  }
  return subContext;
}

/**
 * Parse the `Image` field of an ECS container definition.
 *
 * Three shapes:
 *   - `<account>.dkr.ecr.<region>.amazonaws.com/<repo>:<tag>` — same-account
 *     same-region ECR. Cross-account/region is hard-errored (matches
 *     `cdkl invoke`'s ECR-pull semantics).
 *   - `Fn::Sub` / `Fn::Join` / `Ref` referencing a `Code.fromAsset`-style
 *     CDK asset image. Surfaces `kind: 'cdk-asset'` with the optional
 *     asset hash so the runner can route through `docker-build.ts`.
 *   - Any other public URI (`public.ecr.aws/...`, `docker.io/...`,
 *     `nginx:latest`, etc.) — `kind: 'public'`, runner does a `docker pull`.
 *
 * Two-tier substitution (issue #264):
 *   - **Tier 1** — when `context.pseudoParameters` is populated, AWS pseudo
 *     parameters in `Fn::Sub` bodies (`${AWS::AccountId}` / `${AWS::Region}` /
 *     `${AWS::Partition}` / `${AWS::URLSuffix}`) are substituted before
 *     regex matching. The CLI resolves these via STS + region env once
 *     per invocation.
 *   - **Tier 2** — when `context.stateResources` is populated
 *     (`--from-state`), `${<LogicalId>}` placeholders that resolve to an
 *     `AWS::ECR::Repository` are substituted with the state-recorded
 *     `physicalId`, and `Fn::GetAtt: [<Repo>, 'RepositoryUri']` shapes
 *     are resolved via the same state record.
 *
 * Cross-account / cross-region pull (#455): `pullEcrImage` auto-detects
 * cross-account from `sts:GetCallerIdentity` and authenticates against
 * the URI's region directly. Pass `--ecr-role-arn <arn>` when the caller
 * does not already have cross-account `ecr:GetAuthorizationToken` /
 * `ecr:BatchGetImage` access on the target repository.
 */
function parseContainerImage(
  raw: unknown,
  containerName: string,
  taskLogicalId: string,
  resources: Record<string, TemplateResource>,
  _stack: StackInfo,
  context?: EcsImageResolutionContext
): ResolvedEcsImage {
  // Tier 2: handle `Fn::GetAtt: [<Repo>, 'RepositoryUri']` directly — it
  // produces a complete `<acct>.dkr.ecr.<region>.amazonaws.com/<name>` URI
  // we can match against the ECR regex below. The tag is whatever the
  // template provides; for the bare GetAtt shape there is none, which is
  // unusual but we surface the resulting tagless URI rather than guess.
  const getAttImage = tryResolveImageGetAtt(raw, resources, context);
  if (getAttImage) {
    return classifyResolvedImage(getAttImage);
  }

  // Issue #271: CDK 2.x synthesizes `ContainerImage.fromEcrRepository(repo)`
  // as a `Fn::Join` that builds the ECR URI from nested `Fn::Select` /
  // `Fn::Split` over the repository's `Arn` GetAtt plus a `Ref` to the
  // repo and `Ref: AWS::URLSuffix`. The repository's account-id and
  // region are only available from deployed-stack state (recorded at
  // deploy time), so this shape inherently requires loading state via
  // `--from-cfn-stack` (Tier 2).
  const joinResolved = tryResolveImageFnJoin(raw, resources, context);
  if (joinResolved.kind === 'resolved') {
    return classifyResolvedImage(joinResolved.uri);
  }
  if (joinResolved.kind === 'needs-state') {
    throw new EcsTaskResolutionError(
      `Container '${containerName}' in task '${taskLogicalId}' references same-stack ECR repository '${joinResolved.repoLogicalId}' via Fn::Join. ` +
        `${getEmbedConfig().cliName} cannot resolve the repository URI without state — ` +
        formatStateRemedy(context) +
        ', build via ContainerImage.fromAsset, or pin a public image.'
    );
  }
  if (joinResolved.kind === 'unsupported-join') {
    throw new EcsTaskResolutionError(
      `Container '${containerName}' in task '${taskLogicalId}' has an unsupported Fn::Join Image shape: ${joinResolved.reason}. ` +
        `${getEmbedConfig().cliName} recognizes the canonical CDK 2.x ContainerImage.fromEcrRepository Fn::Join shape ` +
        '(delimiter "" with nested Fn::Select/Fn::Split over an ECR Repository Arn GetAtt + Ref to the repo).'
    );
  }

  const flat = extractImageString(raw);
  if (!flat) {
    throw new EcsTaskResolutionError(
      `Container '${containerName}' in task '${taskLogicalId}' has an unparseable Image property. ` +
        `${getEmbedConfig().cliName} supports flat string images, single-key Fn::Sub bodies, and CDK-asset Image references.`
    );
  }

  // CDK asset shape: contains the bootstrap-assets repo placeholder. The
  // tail `:<hash>` is the asset hash (same shape used by Lambda container
  // images — see `getDockerImageBySourceHash`).
  if (flat.includes('cdk-hnb659fds-container-assets-')) {
    const hashMatch = /:([a-f0-9]{8,})$/.exec(flat);
    const out: ResolvedEcsImage = { kind: 'cdk-asset' };
    if (hashMatch) out.assetHash = hashMatch[1]!;
    return out;
  }

  // Substitute pseudo parameters + same-stack ECR Ref placeholders into
  // the flat string when context is supplied. Pure string-rewrite —
  // unresolved placeholders pass through verbatim so the post-walk
  // diagnostics below can route to the precise error message.
  const substituted = substituteImagePlaceholders(flat, resources, context);

  // Unresolved `${...}` placeholders survived substitution. Surface a
  // precise hint BEFORE the ECR regex match — otherwise a leftover
  // `${MyRepo}` in the path portion would still match the host-portion
  // regex and silently produce a broken URI for `docker pull` to fail on.
  if (substituted.includes('${')) {
    const unresolvedRepoRef = findUnresolvedEcrRepositoryRef(substituted, resources);
    if (unresolvedRepoRef) {
      throw new EcsTaskResolutionError(
        `Container '${containerName}' in task '${taskLogicalId}' references same-stack ECR repository '${unresolvedRepoRef}'. ` +
          `${getEmbedConfig().cliName} cannot resolve the repository URI without state — ` +
          formatStateRemedy(context) +
          ', build via ContainerImage.fromAsset, or pin a public image.'
      );
    }
    if (substituted.includes('AWS::')) {
      throw new EcsTaskResolutionError(
        `Container '${containerName}' in task '${taskLogicalId}' has an Image that references AWS pseudo parameters (${substituted}). ` +
          `${getEmbedConfig().productName} could not resolve them: confirm AWS credentials are configured so STS GetCallerIdentity succeeds, ` +
          'and that --region / AWS_REGION names the target region. ' +
          'Workaround: build the image locally (ContainerImage.fromAsset) or pin a public image.'
      );
    }
    // Some other placeholder survived (e.g. a Parameter ref). Surface as
    // a generic resolver failure rather than letting it slip through as
    // a "public" image.
    throw new EcsTaskResolutionError(
      `Container '${containerName}' in task '${taskLogicalId}' has an Image with unresolved \${...} placeholders (${substituted}). ` +
        `${getEmbedConfig().cliName} only resolves AWS pseudo parameters and same-stack AWS::ECR::Repository refs.`
    );
  }

  // Account-scoped ECR repo.
  const ecrMatch = /^(\d{12})\.dkr\.ecr\.([^.]+)\.amazonaws\.com(?:\.cn)?\//.exec(substituted);
  if (ecrMatch) {
    return { kind: 'ecr', uri: substituted, account: ecrMatch[1]!, region: ecrMatch[2]! };
  }

  return { kind: 'public', uri: substituted };
}

/**
 * When the flat string still contains `${X}` after substitution, check
 * whether `X` (or `X.<attr>`) names a same-stack `AWS::ECR::Repository`.
 * Returns the logical ID of the offending repo for a precise error
 * message; `undefined` otherwise.
 */
function findUnresolvedEcrRepositoryRef(
  substituted: string,
  resources: Record<string, TemplateResource>
): string | undefined {
  const placeholderRegex = /\$\{([^}]+)\}/g;
  let m: RegExpExecArray | null;
  while ((m = placeholderRegex.exec(substituted)) !== null) {
    const key = m[1]!;
    if (key.startsWith('AWS::')) continue;
    const dot = key.indexOf('.');
    const lid = dot === -1 ? key : key.slice(0, dot);
    if (resources[lid]?.Type === 'AWS::ECR::Repository') return lid;
  }
  return undefined;
}

/**
 * Classify a fully-substituted image URI (Tier 1 / Tier 2 produced a
 * concrete string) into the `ResolvedEcsImage` shape. Splits out so the
 * `Fn::GetAtt` path can share the regex-match branches.
 */
function classifyResolvedImage(uri: string): ResolvedEcsImage {
  if (uri.includes('cdk-hnb659fds-container-assets-')) {
    const hashMatch = /:([a-f0-9]{8,})$/.exec(uri);
    const out: ResolvedEcsImage = { kind: 'cdk-asset' };
    if (hashMatch) out.assetHash = hashMatch[1]!;
    return out;
  }
  const ecrMatch = /^(\d{12})\.dkr\.ecr\.([^.]+)\.amazonaws\.com(?:\.cn)?\//.exec(uri);
  if (ecrMatch) {
    return { kind: 'ecr', uri, account: ecrMatch[1]!, region: ecrMatch[2]! };
  }
  return { kind: 'public', uri };
}

/**
 * Handle the discrete `Fn::GetAtt: [<Repo>, 'RepositoryUri']` /
 * `'<Repo>.RepositoryUri'` shape against state-recorded resources. CDK
 * occasionally emits this instead of `Fn::Sub` when the user writes
 * `ContainerImage.fromEcrRepository(repo, tag)` and the tag is a literal.
 * Returns the resolved URI string on hit, `undefined` on miss (the caller
 * falls through to `extractImageString` for `Fn::Sub` / `Fn::Join` / `Ref`).
 */
function tryResolveImageGetAtt(
  raw: unknown,
  resources: Record<string, TemplateResource>,
  context: EcsImageResolutionContext | undefined
): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  const arg = obj['Fn::GetAtt'];
  if (arg === undefined) return undefined;

  let logicalId: string | undefined;
  let attr: string | undefined;
  if (
    Array.isArray(arg) &&
    arg.length === 2 &&
    typeof arg[0] === 'string' &&
    typeof arg[1] === 'string'
  ) {
    logicalId = arg[0];
    attr = arg[1];
  } else if (typeof arg === 'string') {
    const dot = arg.indexOf('.');
    if (dot > 0 && dot < arg.length - 1) {
      logicalId = arg.slice(0, dot);
      attr = arg.slice(dot + 1);
    }
  }
  if (!logicalId || !attr) return undefined;

  const refResource = resources[logicalId];
  if (refResource?.Type !== 'AWS::ECR::Repository') return undefined;
  if (attr !== 'RepositoryUri' && attr !== 'Arn') return undefined;

  const cached = context?.stateResources?.[logicalId]?.attributes?.[attr];
  if (typeof cached === 'string' && cached.length > 0) return cached;
  return undefined;
}

function extractImageString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const sub = obj['Fn::Sub'];
    if (typeof sub === 'string' && sub.length > 0) return sub;
    if (Array.isArray(sub) && typeof sub[0] === 'string') return sub[0];
    const join = obj['Fn::Join'];
    if (Array.isArray(join) && join.length === 2 && Array.isArray(join[1])) {
      const sep = typeof join[0] === 'string' ? join[0] : '';
      const parts = (join[1] as unknown[])
        .map((p) => (typeof p === 'string' ? p : extractImageString(p)))
        .filter((p): p is string => p !== undefined);
      if (parts.length === (join[1] as unknown[]).length) return parts.join(sep);
    }
  }
  return undefined;
}

// `tryResolveImageFnJoin` (plus supporting helpers `findEcrRepositoryRefInTree` /
// `resolveImageIntrinsic` / `resolveImageIntrinsicAny`) and the
// `FnJoinResolveOutcome` type were extracted to `intrinsic-image.ts` so
// `lambda-resolver.ts` can reuse them for container Lambdas (`Code.ImageUri`).
// Both call sites import the shared helper at the top of their respective
// resolver. See issue #286 Gap 2 and PR #280 for the original ECS shape.

function parseVolume(
  raw: unknown,
  idx: number,
  taskLogicalId: string,
  subContext?: SubstitutionContext
): ResolvedEcsVolume {
  if (!raw || typeof raw !== 'object') {
    throw new EcsTaskResolutionError(`Task '${taskLogicalId}' Volumes[${idx}] is not an object.`);
  }
  const v = raw as Record<string, unknown>;
  const name = pickString(v['Name']);
  if (!name) {
    throw new EcsTaskResolutionError(`Task '${taskLogicalId}' Volumes[${idx}] has no Name.`);
  }

  if (v['EFSVolumeConfiguration']) {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' Volumes[${idx}] '${name}' uses EFSVolumeConfiguration, which ${getEmbedConfig().cliName} cannot proxy locally. ` +
        `Workaround: bind-mount a local directory at the same containerPath via Host: { SourcePath: '<local-path>' }, or override at runtime via --env-vars semantics for a Phase 2 follow-up.`
    );
  }
  if (v['FSxWindowsFileServerVolumeConfiguration']) {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' Volumes[${idx}] '${name}' uses FSxWindowsFileServerVolumeConfiguration, which ${getEmbedConfig().cliName} cannot proxy locally.`
    );
  }

  const dockerCfg = v['DockerVolumeConfiguration'];
  if (dockerCfg && typeof dockerCfg === 'object') {
    const d = dockerCfg as Record<string, unknown>;
    const scope = pickString(d['Scope']) === 'shared' ? 'shared' : 'task';
    const cfg: ResolvedEcsVolume['dockerVolumeConfig'] = { scope };
    if (typeof d['Autoprovision'] === 'boolean') cfg.autoprovision = d['Autoprovision'];
    const driver = pickString(d['Driver']);
    if (driver) cfg.driver = driver;
    if (d['DriverOpts'] && typeof d['DriverOpts'] === 'object') {
      const opts: Record<string, string> = {};
      for (const [k, val] of Object.entries(d['DriverOpts'] as Record<string, unknown>)) {
        if (typeof val === 'string') opts[k] = val;
      }
      cfg.driverOpts = opts;
    }
    if (d['Labels'] && typeof d['Labels'] === 'object') {
      const labels: Record<string, string> = {};
      for (const [k, val] of Object.entries(d['Labels'] as Record<string, unknown>)) {
        if (typeof val === 'string') labels[k] = val;
      }
      cfg.labels = labels;
    }
    return { name, kind: 'docker', dockerVolumeConfig: cfg };
  }

  // Host bind mount (or anonymous when SourcePath unset).
  // SourcePath may be a flat string OR a CFn intrinsic (`Fn::Sub` /
  // `Fn::Join` / `Ref`) — CDK emits the intrinsic shape when the user
  // builds the path via `cdk.Fn.sub(...)` / `cdk.Fn.join(...)` (verified
  // via real `cdk synth` against `new ecs.Ec2TaskDefinition({ volumes:
  // [{ name, host: { sourcePath: cdk.Fn.sub('...') } }] })`). When state
  // + pseudo parameters are available (`--from-state` flow on
  // `cdkl run-task` already populates the context for env / secret
  // / image resolution), we substitute the intrinsic in place via the
  // same `substituteAgainstState` helper. Unresolvable intrinsics
  // hard-error — a Host bind mount with a missing path is not safe to
  // proxy as an anonymous volume (the container would silently see an
  // empty mount instead of the data it expects). Closes Gap 6 of #286.
  const host = v['Host'];
  if (host && typeof host === 'object') {
    const rawSourcePath = (host as Record<string, unknown>)['SourcePath'];
    const sourcePath = resolveVolumeSourcePath(rawSourcePath, name, idx, taskLogicalId, subContext);
    if (sourcePath !== undefined) {
      const abs = isAbsolute(sourcePath) ? sourcePath : resolve(process.cwd(), sourcePath);
      return { name, kind: 'host', hostPath: abs };
    }
  }
  return { name, kind: 'host' };
}

/**
 * Resolve a volume `Host.SourcePath` value to a flat host-path string.
 * Handles flat strings (pre-PR behavior) and the CFn intrinsic shapes
 * (`Fn::Sub` / `Fn::Join` / `Ref` / `Fn::GetAtt`) that CDK emits when
 * the user builds the path via `cdk.Fn.sub(...)` / `cdk.Fn.join(...)`.
 * Returns `undefined` when the field is absent or empty (anonymous
 * volume — the legacy path); throws `EcsTaskResolutionError` when the
 * intrinsic cannot be resolved against the supplied context.
 *
 * The hard-error on unresolvable intrinsics matches the upstream
 * pre-Gap-6 behavior (which silently dropped the SourcePath and produced
 * an anonymous volume) only on the failure mode — but now with a precise
 * actionable message instead of mysteriously empty mounts at runtime.
 */
function resolveVolumeSourcePath(
  raw: unknown,
  volumeName: string,
  idx: number,
  taskLogicalId: string,
  subContext: SubstitutionContext | undefined
): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string') return raw.length > 0 ? raw : undefined;
  if (typeof raw !== 'object') {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' Volumes[${idx}] '${volumeName}' Host.SourcePath must be a string or a CFn intrinsic, got ${typeof raw}.`
    );
  }
  if (!subContext) {
    throw new EcsTaskResolutionError(
      `Task '${taskLogicalId}' Volumes[${idx}] '${volumeName}' Host.SourcePath is a CFn intrinsic (Ref/Fn::Sub/Fn::Join/Fn::GetAtt). ` +
        `Pass --from-state to substitute it against the deployed state + AWS pseudo parameters.`
    );
  }
  const sub = substituteAgainstState(raw, subContext);
  if (sub.kind === 'literal' && typeof sub.value === 'string' && sub.value.length > 0) {
    return sub.value;
  }
  throw new EcsTaskResolutionError(
    `Task '${taskLogicalId}' Volumes[${idx}] '${volumeName}' Host.SourcePath could not be resolved: ` +
      (sub.kind === 'literal' ? 'resolved to non-string / empty value' : sub.reason)
  );
}

/**
 * Synth-time placeholder marker used in the account-id segment of an ARN
 * when the role's account cannot be known statically (the role is defined
 * inline as an `AWS::IAM::Role` in the same stack). The CLI replaces this
 * with the live account via STS `GetCallerIdentity` lazily — only when
 * `--assume-task-role` is used in its bare form and the resolved ARN
 * still contains this marker.
 */
export const TASK_ROLE_ACCOUNT_PLACEHOLDER = '${AWS::AccountId}';

/**
 * Resolve a Task / Execution role ARN reference. Accepts:
 *
 *   - A flat string ARN — returned verbatim.
 *   - `Ref` / `Fn::GetAtt[..., 'Arn']` against an `AWS::IAM::Role` in
 *     the same stack — returns a synth-time placeholder ARN of the shape
 *     `arn:aws:iam::${AWS::AccountId}:role/<RoleLogicalId>` (the CLI
 *     fills the account segment in lazily via STS when the bare
 *     `--assume-task-role` form is used).
 *   - `Fn::Join` / `Fn::Sub` — when the supplied substitution context
 *     can resolve every nested intrinsic to a literal (state + pseudo
 *     parameters via `--from-state` flow), the resulting flat ARN is
 *     returned. This covers the rare `iam.Role.fromRoleArn(scope, id,
 *     cdk.Fn.join(...))` pattern verified via real `cdk synth` on
 *     2026-05-12 against `iam.Role.fromRoleArn(stack, 'X', Fn.join(':',
 *     ['arn','aws','iam','',cdk.Aws.ACCOUNT_ID,'role/Name']))`.
 *     Closes Gap 5 of #286.
 *
 * Returns `undefined` when the reference cannot be resolved (logical id
 * missing, non-IAM-Role target, unsupported intrinsic shape, or
 * intrinsic shape whose nested refs cannot be resolved without
 * `--from-state`).
 */
function resolveRoleArn(
  value: unknown,
  resources: Record<string, TemplateResource>,
  subContext?: SubstitutionContext
): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return undefined;
  const obj = value as Record<string, unknown>;

  let refLogicalId: string | undefined;
  if ('Ref' in obj && typeof obj['Ref'] === 'string') {
    refLogicalId = obj['Ref'];
  } else if ('Fn::GetAtt' in obj) {
    const arg = obj['Fn::GetAtt'];
    if (Array.isArray(arg) && typeof arg[0] === 'string') {
      // Accept `[<LogicalId>, 'Arn']`; other attribute names (rare on IAM
      // Role refs) fall through to undefined since the placeholder we emit
      // is always the role ARN shape.
      const attr = typeof arg[1] === 'string' ? arg[1] : '';
      if (attr === '' || attr === 'Arn') refLogicalId = arg[0];
    }
  }
  if (refLogicalId !== undefined) {
    const role = resources[refLogicalId];
    if (role?.Type !== 'AWS::IAM::Role') return undefined;
    return `arn:aws:iam::${TASK_ROLE_ACCOUNT_PLACEHOLDER}:role/${refLogicalId}`;
  }

  // `Fn::Join` / `Fn::Sub` — `iam.Role.fromRoleArn(stack, id,
  // cdk.Fn.join(...))` shape. Resolve via state + pseudo parameters if
  // available; fall back to undefined (silent-drop, pre-PR behavior)
  // when the context can't fully resolve the intrinsic — same posture
  // as the existing Ref-to-missing-resource path.
  if ('Fn::Join' in obj || 'Fn::Sub' in obj) {
    if (!subContext) return undefined;
    const sub = substituteAgainstState(value, subContext);
    if (sub.kind === 'literal' && typeof sub.value === 'string' && sub.value.length > 0) {
      return sub.value;
    }
    return undefined;
  }

  return undefined;
}

function pickString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function pickStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === 'string') out.push(v);
  }
  return out;
}

function notFoundError(
  target: string,
  stack: StackInfo,
  resources: Record<string, TemplateResource>
): EcsTaskResolutionError {
  const tasks: { displayPath: string; logicalId: string }[] = [];
  for (const [logicalId, resource] of Object.entries(resources)) {
    if (resource.Type !== 'AWS::ECS::TaskDefinition') continue;
    const meta = resource.Metadata;
    const cdkPath = typeof meta?.['aws:cdk:path'] === 'string' ? meta['aws:cdk:path'] : '';
    tasks.push({ displayPath: cdkPath || logicalId, logicalId });
  }
  let msg = `target '${target}' did not match any ECS task definition in ${stack.stackName}.\n\n`;
  if (tasks.length === 0) {
    msg += `Stack ${stack.stackName} has no AWS::ECS::TaskDefinition resources.`;
  } else {
    const width = Math.max(...tasks.map((t) => t.displayPath.length));
    msg += `Available task definitions in ${stack.stackName}:\n`;
    for (const t of tasks) {
      msg += `  ${t.displayPath.padEnd(width)}  (${t.logicalId})\n`;
    }
  }
  return new EcsTaskResolutionError(msg.trimEnd());
}

/**
 * Resolve a `kind: 'cdk-asset'` Image entry back to the on-disk build
 * context recorded in the stack's asset manifest. Surfaces an absolute
 * path to the cdk.out asset directory + the dockerfile name so the
 * runner can hand the pair to `buildDockerImage` directly. Returns
 * `undefined` when the asset isn't in the manifest — the caller hard-
 * errors with a clear "re-synthesize" pointer.
 */
export function buildCdkOutDir(stack: StackInfo): string | undefined {
  if (!stack.assetManifestPath) return undefined;
  return dirname(stack.assetManifestPath);
}

/**
 * Verify that a directory referenced by a docker-volume `Host.SourcePath`
 * actually exists. Surfaced as a warning rather than a hard error so
 * users can bind-mount future-created paths.
 */
export function checkVolumeHostPath(hostPath: string): boolean {
  try {
    return existsSync(hostPath) && statSync(hostPath).isDirectory();
  } catch {
    return false;
  }
}

/**
 * Async post-pass that walks the resolved task's container Environment +
 * Secrets entries from the raw template (preserved in `task.resource`)
 * and re-attempts substitution via {@link substituteAgainstStateAsync}
 * against the supplied context. The sync `parseContainerDefinition`
 * pass already substituted everything the legacy resolver handles; this
 * pass picks up the additional shapes the async resolver supports —
 * specifically `Fn::ImportValue` / `Fn::GetStackOutput` (issue #454).
 *
 * Resolved entries are patched onto the container's `environment` /
 * `secrets` map AND the corresponding `warnings` entries are filtered
 * out so the CLI doesn't print a stale per-container warn for an entry
 * the post-pass successfully resolved. Entries that STILL can't resolve
 * (e.g. producer stack not deployed) keep their original warning so the
 * CLI's UX matches the sync path.
 *
 * Pure-functional on the task structure outside of in-place mutation of
 * the container's `environment` / `secrets` / `warnings` arrays. The
 * task is expected to be the same `ResolvedEcsTask` instance returned
 * by `resolveEcsTaskTarget` — the runner downstream reads from these
 * fields directly.
 */
export async function applyCrossStackResolverToTask(
  task: ResolvedEcsTask,
  context: SubstitutionContext
): Promise<void> {
  if (!context.crossStackResolver) return;
  const props = task.resource.Properties ?? {};
  const rawContainers = props['ContainerDefinitions'];
  if (!Array.isArray(rawContainers)) return;

  for (let idx = 0; idx < task.containers.length; idx += 1) {
    const container = task.containers[idx]!;
    const raw = rawContainers[idx];
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as Record<string, unknown>;
    const containerName = pickString(c['Name']) ?? container.name;

    // Track keys the post-pass resolved so we can filter the matching
    // entries out of `container.warnings` (which the sync pass populated
    // with "dropped: <reason>" lines).
    const resolvedEnvKeys = new Set<string>();
    const resolvedSecretNames = new Set<string>();

    // Environment[].Value
    if (Array.isArray(c['Environment'])) {
      for (const entry of c['Environment'] as unknown[]) {
        if (!entry || typeof entry !== 'object') continue;
        const e = entry as Record<string, unknown>;
        const key = pickString(e['Name']);
        const value = e['Value'];
        if (!key) continue;
        // Skip literal values + entries already resolved by the sync
        // pass (present in `container.environment`).
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          continue;
        }
        if (key in container.environment) continue;
        if (!isCrossStackIntrinsic(value)) {
          // The sync pass already tried this — re-trying here can't
          // produce a different outcome.
          continue;
        }
        const { result: sub, consumedSensitive } = await resolveWithSensitivityAsync(
          value,
          context
        );
        if (sub.kind === 'literal') {
          container.environment[key] = String(sub.value);
          resolvedEnvKeys.add(key);
          // SecureString-backed value — keep it off the `docker run` argv (#99).
          if (consumedSensitive && !container.sensitiveEnvKeys.includes(key)) {
            container.sensitiveEnvKeys.push(key);
          }
        }
      }
    }

    // Secrets[].ValueFrom
    if (Array.isArray(c['Secrets'])) {
      for (const entry of c['Secrets'] as unknown[]) {
        if (!entry || typeof entry !== 'object') continue;
        const e = entry as Record<string, unknown>;
        const sName = pickString(e['Name']);
        const valueFromRaw = e['ValueFrom'];
        if (!sName) continue;
        if (typeof valueFromRaw === 'string' && valueFromRaw.length > 0) continue;
        if (container.secrets.some((s) => s.name === sName)) continue;
        if (!isCrossStackIntrinsic(valueFromRaw)) continue;
        const sub = await substituteAgainstStateAsync(valueFromRaw, context);
        if (sub.kind === 'literal' && typeof sub.value === 'string' && sub.value.length > 0) {
          container.secrets.push({ name: sName, valueFrom: sub.value });
          resolvedSecretNames.add(sName);
        }
      }
    }

    if (resolvedEnvKeys.size > 0 || resolvedSecretNames.size > 0) {
      container.warnings = container.warnings.filter((w) => {
        for (const k of resolvedEnvKeys) {
          if (w.startsWith(`Environment '${k}' dropped:`)) return false;
        }
        for (const k of resolvedSecretNames) {
          if (w.startsWith(`Secret '${k}' dropped:`)) return false;
        }
        return true;
      });
      // Same filter at the task-level (the per-container warnings are
      // re-emitted onto `task.warnings` by `parseEcsTaskFromCdkResource`,
      // prefixed with `Container '<name>': `).
      task.warnings = task.warnings.filter((w) => {
        for (const k of resolvedEnvKeys) {
          if (w.startsWith(`Container '${containerName}': Environment '${k}' dropped:`)) {
            return false;
          }
        }
        for (const k of resolvedSecretNames) {
          if (w.startsWith(`Container '${containerName}': Secret '${k}' dropped:`)) {
            return false;
          }
        }
        return true;
      });
    }
  }
}
